import React, { useEffect, useMemo } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Provider as StoreProvider } from '@/lib/Provider';
import { NextIntlClientProvider } from '@/lib/i18n';
import { getInitialLocale, getMessagesForLocale } from '@/lib/messages';
import { NavbarComponent, TemplateProvider } from '@/templates';
import { Toaster } from '@/components/ui/toaster';
import { useThemeInitializer } from '@/hooks/useThemeInitializer';
import { config, getCurrentTemplateName, getTemplateAssetPath } from '@/config';
import HomePage from '@/pages/page';
import BookingPage from '@/pages/booking/page';
import CancellationPage from '@/pages/cancellation/page';
import ConfirmPage from '@/pages/confirm/page';
import FlightDetailsPage from '@/pages/flight-details/page';
import FlightListPage from '@/pages/flight-list/page';
import PaymentPage from '@/pages/payment/page';
import ProfilePage from '@/pages/profile/page';
import ReviewPage from '@/pages/review/page';
import SsrDetailPage from '@/pages/ssr-detail/page';
import TicketPage from '@/pages/ticket/page';
import TravelerInfoPage from '@/pages/traveler-info/page';
import ViewBookingPage from '@/pages/viewBooking/page';
import HotelPage from '@/pages/hotel/page';

const AppRoutes = () => (
  <Routes>
    <Route path="/" element={<HomePage />} />
    <Route path="/flight" element={<Navigate to="/" replace />} />
    <Route path="/hotel" element={<HotelPage />} />
    <Route path="/hotel/*" element={<HotelPage />} />
    <Route path="/booking" element={<BookingPage />} />
    <Route path="/cancellation" element={<CancellationPage />} />
    <Route path="/confirm" element={<ConfirmPage />} />
    <Route path="/flight-details" element={<FlightDetailsPage />} />
    <Route path="/flight-list" element={<FlightListPage />} />
    <Route path="/payment" element={<PaymentPage />} />
    <Route path="/profile" element={<ProfilePage />} />
    <Route path="/review" element={<ReviewPage />} />
    <Route path="/ssr-detail" element={<SsrDetailPage />} />
    <Route path="/ticket" element={<TicketPage />} />
    <Route path="/traveler-info" element={<TravelerInfoPage />} />
    <Route path="/viewBooking" element={<ViewBookingPage />} />
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

const AppShell = () => {
  const locale = getInitialLocale();
  const messages = useMemo(() => getMessagesForLocale(locale), [locale]);
  const templateName = getCurrentTemplateName();
  const { isInitialized } = useThemeInitializer();

  useEffect(() => {
    document.documentElement.lang = locale;
    document.title = config.title || 'B2C Web';
    document.documentElement.className =
      `${config.companyName || 'default'} ${templateName}`.trim();
    document.body.classList.add('font-sans', 'antialiased');

    const faviconHref = getTemplateAssetPath('favicon');
    let favicon = document.querySelector<HTMLLinkElement>("link[rel='icon']");

    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      document.head.appendChild(favicon);
    }

    favicon.href = faviconHref;
  }, [locale, templateName]);

  if (!isInitialized) {
    return <div className="min-h-screen bg-background" />;
  }

  return (
    <StoreProvider>
      <NextIntlClientProvider locale={locale} messages={messages}>
        <TemplateProvider templateName={templateName}>
          <NavbarComponent />
          <AppRoutes />
          <Toaster />
        </TemplateProvider>
      </NextIntlClientProvider>
    </StoreProvider>
  );
};

export default function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}
